import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from '@mui/material';

// Local imports
import { DepartmentSelector } from './DepartmentSelector';
import {
  API_ENDPOINTS,
  DEPARTMENTS,
  LABELS,
  ALERT_MESSAGES,
  UI_CONSTANTS,
  COMPONENT_IDS
} from './SupportRequestTab.constants';
import {
  fetchDepartmentUsers,
  resetSupportSelections,
  createChangeHandler
} from './SupportRequestTab.utils';

export const SupportRequestTab = ({
                                    // ================ State Props ================
                                    needsSupport,
                                    marketingSupport,
                                    supplyChainSupport,
                                    selectedMarketingMember,
                                    selectedSupplyChainMember,

                                    // ================ Setter Props ================
                                    setNeedsSupport,
                                    setMarketingSupport,
                                    setSupplyChainSupport,
                                    setSelectedMarketingMember,
                                    setSelectedSupplyChainMember,

                                    // ================ Other Props ================
                                    formErrors,
                                    theme,
                                    disabled = false
                                  }) => {
  // ================ Local State ================
  const [marketingUsers, setMarketingUsers] = useState([]);
  const [supplyChainUsers, setSupplyChainUsers] = useState([]);
  const [loadingMarketing, setLoadingMarketing] = useState(false);
  const [loadingSupplyChain, setLoadingSupplyChain] = useState(false);

  // ================ Effects ================
  // Fetch marketing users
  useEffect(() => {
    if (needsSupport && marketingSupport) {
      setLoadingMarketing(true);
      fetchDepartmentUsers(API_ENDPOINTS.MARKETING_USERS)
          .then(setMarketingUsers)
          .finally(() => setLoadingMarketing(false));
    }
  }, [needsSupport, marketingSupport]);

  // Fetch supply chain users
// ... continuing from previous
  // Fetch supply chain users
  useEffect(() => {
    if (needsSupport && supplyChainSupport) {
      setLoadingSupplyChain(true);
      fetchDepartmentUsers(API_ENDPOINTS.SUPPLY_CHAIN_USERS)
          .then(setSupplyChainUsers)
          .finally(() => setLoadingSupplyChain(false));
    }
  }, [needsSupport, supplyChainSupport]);

  // ================ Handlers ================
  const handleSupportToggle = (event) => {
    const isChecked = event.target.checked;
    if (setNeedsSupport) {
      setNeedsSupport(isChecked);
    }

    if (!isChecked) {
      resetSupportSelections({
        setMarketingSupport,
        setSupplyChainSupport,
        setSelectedMarketingMember,
        setSelectedSupplyChainMember
      });
    }
  };

  // ================ Main Render ================
  return (
      <Box sx={styles.container}>
        {/* Main Support Toggle */}
        <FormControlLabel
            control={
              <Switch
                  checked={needsSupport}
                  onChange={createChangeHandler(setNeedsSupport, disabled) ? handleSupportToggle : undefined}
                  color="primary"
                  size="small"
                  disabled={disabled}
              />
            }
            label={
              <Typography variant="body2" sx={styles.labelText}>
                {LABELS.REQUEST_SUPPORT}
              </Typography>
            }
            sx={styles.mainToggle}
        />

        {/* Support Options (shown when toggle is on) */}
        {needsSupport && (
            <Box sx={styles.supportOptionsContainer}>
              {/* Info Alert */}
              <Alert severity="info" sx={styles.infoAlert}>
                {ALERT_MESSAGES.SUPPORT_INFO}
              </Alert>

              {/* Teams Selection Label */}
              <Typography variant="body2" color="textSecondary" sx={styles.selectTeamsLabel}>
                {LABELS.SELECT_TEAMS}
              </Typography>

              {/* Department Selectors */}
              <Stack spacing={1} sx={styles.departmentsStack}>
                {/* Marketing Department */}
                <DepartmentSelector
                    department={DEPARTMENTS.MARKETING}
                    label={LABELS.MARKETING}
                    placeholder={LABELS.SELECT_MARKETING_MEMBER}
                    isChecked={marketingSupport}
                    isLoading={loadingMarketing}
                    users={marketingUsers}
                    selectedMember={selectedMarketingMember}
                    onCheckChange={setMarketingSupport}
                    onMemberChange={setSelectedMarketingMember}
                    disabled={disabled}
                    checkboxId={COMPONENT_IDS.MARKETING_CHECKBOX}
                />

                {/* Supply Chain Department */}
                <DepartmentSelector
                    department={DEPARTMENTS.SUPPLY_CHAIN}
                    label={LABELS.SUPPLY_CHAIN}
                    placeholder={LABELS.SELECT_SUPPLY_CHAIN_MEMBER}
                    isChecked={supplyChainSupport}
                    isLoading={loadingSupplyChain}
                    users={supplyChainUsers}
                    selectedMember={selectedSupplyChainMember}
                    onCheckChange={setSupplyChainSupport}
                    onMemberChange={setSelectedSupplyChainMember}
                    disabled={disabled}
                    checkboxId={COMPONENT_IDS.SUPPLY_CHAIN_CHECKBOX}
                />
              </Stack>

              {/* Error Alert */}
              {formErrors.supportTeams && (
                  <Alert severity="error" sx={styles.errorAlert}>
                    {formErrors.supportTeams}
                  </Alert>
              )}

              {/* Review Info Alert */}
              <Alert severity="info" sx={styles.reviewAlert}>
                {ALERT_MESSAGES.TEAMS_REVIEW}
              </Alert>
            </Box>
        )}
      </Box>
  );
};

// ================ Component Styles ================
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  mainToggle: {
    mb: 1
  },
  labelText: {
    fontSize: UI_CONSTANTS.FONT_SIZES.LABEL,
    color: 'text.secondary'
  },
  supportOptionsContainer: {
    pl: 2
  },
  infoAlert: {
    mb: 2
  },
  selectTeamsLabel: {
    fontSize: UI_CONSTANTS.FONT_SIZES.LABEL,
    mb: 1
  },
  departmentsStack: {
    mb: 1
  },
  errorAlert: {
    mt: 0.5,
    py: 0.5,
    fontSize: UI_CONSTANTS.FONT_SIZES.ALERT
  },
  reviewAlert: {
    mt: 0.5,
    py: 0.5,
    fontSize: UI_CONSTANTS.FONT_SIZES.ALERT
  }
};